import { useState } from 'react';
import sortBy from 'lodash/sortBy';

import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Box, DividerSeparator, Stack, Typography } from '@lyearn/ui';
import Feedback from '@/features/feedback';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import useFeedback from '@/modules/corporate/hooks/useFeedback';
import { renderAttachments } from '@/modules/corporate/modules/questions/fields/components/Player/OptionsListItem/renderers';
import { ViewFields } from '@/modules/corporate/modules/questions/fields/view';
import QuestionAnswerFeedback from '@/modules/corporate/modules/quiz/views/QuizPlayer/components/QuizPlayerQuestion/components/QuestionAnswerFeedback';
import QuizPlayerQuestionHeader from '@/modules/corporate/modules/quiz/views/QuizPlayer/components/QuizPlayerQuestion/components/QuizPlayerQuestionHeader';
import QuizReviewMetrics from '@/modules/corporate/modules/quiz/views/QuizPlayer/components/QuizReviewMetrics';
import { fullPagePlayerViewStyles } from '@/modules/corporate/modules/quiz/views/QuizPlayer/utils/styles';
import QuestionEntity from '@/modules/question/entity/Question';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';
import {
  EntityTypeEnum,
  PerformanceStatusEnum,
  QuestionTypeEnum,
  QuizRevealAnswerEnum,
} from '@/types/schema';
import { overflowUnset } from '@/utils/styles';

import { getHTMLTextContent } from '../../../../../QuizEditor/components/QuizEditorSidebar/views/QuestionsList/components/QuestionListItem/utils';
import NextLessonButton from '../NextLessonButton';
import QuizReviewQuestionListItem from '../QuizReviewQuestionListItem';
import { QuizPlayerReviewContentProps } from './types';
import { getQuizReviewMetrics } from './utils';

const QuizPlayerReviewContent = (props: QuizPlayerReviewContentProps) => {
  const { t } = useTranslation();
  const { isMobile } = useScreen();
  const { [FeatureFlags.EnableFeedback]: isFeedbackEnabled } = useFeatureFlags();
  const isAdmin = useAdminRoute();
  const {
    quiz,
    quizAttempt,
    questionAttemptById,
    isPreview,
    correctAnswersByQuestionId,
    quizUser,
    className,
    isEmbed,
    quizPerformance,
    fetchCorrectAnswers,
  } = props;

  const canReviewQuestions =
    quiz.settings?.revealAnswers !== QuizRevealAnswerEnum.Never && fetchCorrectAnswers;
  const userContentFeedbackId = quizPerformance?.userContentFeedback?.id;
  const { showFeedback } = useFeedback({
    performanceStatus: PerformanceStatusEnum.Completed,
    userContentFeedbackId,
  });

  const [currentQuestionIndex, setQuestionIndex] = useState(0);
  const currentQuestion = quiz.quizQuestionsConfig.questions[currentQuestionIndex];
  const { Player, getQuestionLabel } = ViewFields[currentQuestion.questionType];
  const currentQuestionAttempt = questionAttemptById[currentQuestion.id];
  const currentQuestionCorrectAnswers = correctAnswersByQuestionId[currentQuestion.id];

  const renderReviewMobileView = () => (
    <Stack className="mt-40 px-24 w-full" direction="col">
      <Typography variant="heading-s">{t('QuizPlayer:ReviewQuestions_plural')}</Typography>
      <DividerSeparator className="mt-30" direction="col" gapY={20}>
        {quiz.quizQuestionsConfig.questions.map((question, index) => {
          const questionAttempt = questionAttemptById[question.id];
          const correctAnswer = correctAnswersByQuestionId[question.id];
          const { Review } = ViewFields[question.questionType];

          return (
            <Stack key={question.id} direction="row">
              <Typography color="text-text-paragraph" variant="body-short-01">
                {`${index + 1}.`}
              </Typography>
              <Stack className="w-full" direction="col" gapY={12}>
                <Typography className="ms-8" color="text-text-paragraph" variant="label-01">
                  {QuestionEntity.questionTextLabel(
                    question.questionText
                      ? getHTMLTextContent(question.questionText)
                      : t('QuizCreator:UntitledQuestion'),
                  )}
                </Typography>
                {question.questionType !== QuestionTypeEnum.ImageHotspot
                  ? renderAttachments({ attachments: question?.attachments })
                  : null}
                <Review
                  correctAnswers={canReviewQuestions ? correctAnswer : undefined}
                  question={question}
                  questionAttempt={{
                    submission: questionAttempt?.submission,
                    isSubmitted: canReviewQuestions,
                  }}
                />
                {canReviewQuestions ? (
                  <QuestionAnswerFeedback
                    className="mt-4 xs:rounded-6 xs:p-12"
                    correctAnswerFeedback={question.correctAnswerFeedback}
                    evaluationStatus={questionAttempt?.evaluationStatus}
                    incorrectAnswerFeedback={question.incorrectAnswerFeedback}
                    variant="body-short-02"
                  />
                ) : null}
              </Stack>
            </Stack>
          );
        })}
      </DividerSeparator>
    </Stack>
  );

  const renderReviewWebView = () => (
    <Stack
      key={currentQuestion.id}
      className={cn('mx-auto w-full max-w-[55.2rem] flex-1 xs:px-16', fullPagePlayerViewStyles)}
      direction="col">
      <QuizPlayerQuestionHeader
        questionLabel={getQuestionLabel({ questionSubType: currentQuestion.subType })}
      />
      <Player
        correctAnswers={canReviewQuestions ? currentQuestionCorrectAnswers : undefined}
        question={currentQuestion}
        questionAttempt={{
          submission: currentQuestionAttempt?.submission,
          isSubmitted: canReviewQuestions,
        }}
      />
      {canReviewQuestions ? (
        <QuestionAnswerFeedback
          className="mt-32 px-8"
          correctAnswerFeedback={currentQuestion.correctAnswerFeedback}
          evaluationStatus={currentQuestionAttempt?.evaluationStatus}
          incorrectAnswerFeedback={currentQuestion.incorrectAnswerFeedback}
        />
      ) : null}
    </Stack>
  );

  return (
    <Stack
      alignItems={isMobile ? 'center' : undefined}
      className={cn('flex-1 overflow-auto xs:flex-col xs:pb-160', className)}>
      <Stack
        className="max-w-[40rem] w-full border-r border-border ps-20 xs:border-none"
        direction="col">
        <QuizReviewMetrics
          {...getQuizReviewMetrics({ quiz, quizAttempt, questionAttemptById })}
          quizUser={quizUser}
        />
        {isMobile ? null : (
          <Stack
            className="mt-32 w-full flex-1 overflow-y-auto pb-20 pe-20"
            direction="col"
            gapY={4}>
            {quiz.quizSections?.length ? (
              <>
                {(() => {
                  const sortedSections = sortBy(quiz.quizSections, 'order');
                  const sectionById = new Map(sortedSections.map((s) => [s._id, s]));
                  const configByQuestionId = new Map(
                    quiz.quizQuestionsConfig.quizQuestions.map((qq) => [qq.questionId, qq]),
                  );

                  const groupedBySectionId = new Map<
                    string,
                    typeof quiz.quizQuestionsConfig.questions
                  >();
                  const unsectioned: typeof quiz.quizQuestionsConfig.questions = [];

                  quiz.quizQuestionsConfig.questions.forEach((question) => {
                    const cfg = configByQuestionId.get(question.id);
                    const sectionId = cfg?.sectionId ?? null;
                    if (!sectionId || !sectionById.has(sectionId)) {
                      unsectioned.push(question);
                      return;
                    }

                    const existing = groupedBySectionId.get(sectionId) ?? [];
                    existing.push(question);
                    groupedBySectionId.set(sectionId, existing);
                  });

                  return (
                    <>
                      {sortedSections.map((section) => (
                        <Box key={section._id}>
                          <Typography
                            className="px-12 py-6"
                            color="text-text-secondary"
                            variant="label-03">
                            {section.name}
                          </Typography>
                          {(groupedBySectionId.get(section._id) ?? []).map((question) => {
                            const index = quiz.quizQuestionsConfig.questions.findIndex(
                              (q) => q.id === question.id,
                            );
                            const questionAttempt = questionAttemptById[question.id];
                            return (
                              <QuizReviewQuestionListItem
                                key={question.id}
                                evaluationStatus={
                                  canReviewQuestions ? questionAttempt?.evaluationStatus : null
                                }
                                isSelected={currentQuestionIndex === index}
                                question={question}
                                questionIndex={index}
                                onClick={setQuestionIndex}
                              />
                            );
                          })}
                        </Box>
                      ))}
                      {unsectioned.map((question) => {
                        const index = quiz.quizQuestionsConfig.questions.findIndex(
                          (q) => q.id === question.id,
                        );
                        const questionAttempt = questionAttemptById[question.id];
                        return (
                          <QuizReviewQuestionListItem
                            key={question.id}
                            evaluationStatus={
                              canReviewQuestions ? questionAttempt?.evaluationStatus : null
                            }
                            isSelected={currentQuestionIndex === index}
                            question={question}
                            questionIndex={index}
                            onClick={setQuestionIndex}
                          />
                        );
                      })}
                    </>
                  );
                })()}
              </>
            ) : (
              quiz.quizQuestionsConfig.questions.map((question, index) => {
                const questionAttempt = questionAttemptById[question.id];
                return (
                  <QuizReviewQuestionListItem
                    key={question.id}
                    evaluationStatus={canReviewQuestions ? questionAttempt?.evaluationStatus : null}
                    isSelected={currentQuestionIndex === index}
                    question={question}
                    questionIndex={index}
                    onClick={setQuestionIndex}
                  />
                );
              })
            )}
          </Stack>
        )}
      </Stack>
      <Stack
        alignItems="end"
        className={cn('flex-1 overflow-y-auto w-full', {
          [overflowUnset]: isMobile,
          'xs:mb-128': isEmbed && isMobile,
        })}
        direction="col">
        {isMobile ? renderReviewMobileView() : renderReviewWebView()}
        {!isPreview && showFeedback ? (
          <Feedback
            containerClassName="sticky bottom-16 rounded-8 border border-border bg-bg-primary py-6 px-12 shadow-01 me-16 xs:static xs:me-0 xs:mt-40"
            entityId={quiz.id}
            entityType={EntityTypeEnum.Quiz}
            feedbackQuestions={quiz.feedbackQuestions}
          />
        ) : null}
      </Stack>
      {isMobile && isEmbed ? <NextLessonButton /> : null}
    </Stack>
  );
};

export default QuizPlayerReviewContent;
